import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Upload, RefreshCw, FileArchive, Landmark } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import usePermissions from '../../hooks/usePermissions';
import useRememberedInputs from '../../hooks/useRememberedInputs';
import { BankTxDialog, money } from './BankTxDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ['', 'unmatched', 'suggested', 'matched', 'ignored'];
const TONE = { matched: 'bg-emerald-100 text-emerald-800', suggested: 'bg-amber-100 text-amber-800', unmatched: 'bg-red-100 text-red-700', ignored: 'bg-slate-100 text-slate-600' };
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

const download = async (url, params, fallback) => {
  const r = await axios.get(url, { params, responseType: 'blob' });
  const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || fallback;
  const href = URL.createObjectURL(r.data);
  const a = Object.assign(document.createElement('a'), { href, download: name });
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
};

const TxRow = ({ tx, onOpen }) => {
  const { t } = useTranslation();
  const m = tx.matches || [];
  return (
    <tr className="cursor-pointer border-b hover:bg-slate-50" onClick={onOpen} data-testid={`bank-tx-${tx.id}`}>
      <td className="px-2 py-2 text-xs whitespace-nowrap">{String(tx.booking_date).slice(0, 10)}</td>
      <td className={`px-2 py-2 text-right text-sm tabular-nums whitespace-nowrap ${tx.amount > 0 ? 'text-emerald-700' : ''}`}>{money(tx.amount, tx.currency)}</td>
      <td className="px-2 py-2 text-xs max-w-[14rem]"><div className="truncate font-medium">{tx.counterparty_name || '—'}</div><div className="truncate text-slate-500">{tx.title}</div></td>
      <td className="px-2 py-2"><Badge className={`${TONE[tx.status]} text-[10px]`} data-testid={`bank-tx-status-${tx.id}`}>{t(`bank.status.${tx.status}`)}</Badge></td>
      <td className="px-2 py-2 text-xs max-w-[16rem]">
        {m.length ? m.map((x) => <div key={`${x.type}-${x.id}`} className="truncate">{t(`bank.types.${x.type}`)} · {x.label}</div>)
          : (tx.suggestions || []).length ? <span className="text-amber-700">{t('bank.nSuggestions', { count: tx.suggestions.length })}</span> : <span className="text-slate-400">—</span>}
      </td>
    </tr>
  );
};

/** Bank statements: import, automatic matching, review and the reconciliation pack. */
export const BankReconciliation = () => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canEdit = can('budget.edit');
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ rows: [], counts: {}, total: 0 });
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useRememberedInputs('bank-reconciliation', { start: [start, setStart], end: [end, setEnd], status: [status, setStatus] });
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bank/transactions`, { params: { start_date: start || undefined, end_date: end || undefined, status: status || undefined, q: q || undefined, limit: 500 } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || t('bank.failed')); }
  }, [start, end, status, q, t]);
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/bank/statements/import`, fd);
      toast.success(t('bank.imported', r.data));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t('bank.failed')); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const rematch = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/bank/rematch`, { start_date: start || null, end_date: end || null }); toast.success(t('bank.rematched', r.data)); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t('bank.failed')); }
    finally { setBusy(false); }
  };
  const exportPack = async () => {
    if (!start || !end) { toast.error(t('bank.pickPeriod')); return; }
    setBusy(true);
    try { await download(`${API}/bank/reconciliation-export`, { start_date: start, end_date: end }, 'bank_reconciliation.zip'); }
    catch { toast.error(t('bank.exportFailed')); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="bank-reconciliation">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg"><Landmark className="h-5 w-5" />{t('bank.title')}</CardTitle>
        <CardDescription>{t('bank.hint')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">{t('budget.from')}</Label><Input type="date" className="h-8 w-40" value={start} onChange={(e) => setStart(e.target.value)} data-testid="bank-start" /></div>
          <div><Label className="text-xs">{t('budget.to')}</Label><Input type="date" className="h-8 w-40" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="bank-end" /></div>
          <Input className="h-8 w-56" placeholder={t('bank.filterPh')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="bank-filter" />
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            {canEdit && <>
              <input ref={fileRef} type="file" accept=".sta,.mt940,.940,.txt,.csv,.xls,.xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="bank-file-input" />
              <Button size="sm" className="h-8" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="bank-import-btn"><Upload className="h-4 w-4 mr-1" />{t('bank.import')}</Button>
              <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={rematch} data-testid="bank-rematch-btn"><RefreshCw className="h-4 w-4 mr-1" />{t('bank.rematch')}</Button>
            </>}
            <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={exportPack} data-testid="bank-export-btn"><FileArchive className="h-4 w-4 mr-1" />{t('bank.export')}</Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="bank-status-filter">
          {STATUSES.map((s) => (
            <Button key={s || 'all'} size="sm" variant={status === s ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setStatus(s)} data-testid={`bank-status-${s || 'all'}`}>
              {s ? t(`bank.status.${s}`) : t('bank.all')}{s && ` (${data.counts?.[s] || 0})`}
            </Button>
          ))}
        </div>
        <div className="overflow-x-auto rounded border">
          <table className="w-full min-w-[720px] text-left">
            <thead className="bg-slate-50 text-[11px] text-slate-600"><tr>
              <th className="px-2 py-2">{t('bank.cols.date')}</th><th className="px-2 text-right">{t('bank.cols.amount')}</th><th className="px-2">{t('bank.cols.counterparty')}</th>
              <th className="px-2">{t('bank.cols.status')}</th><th className="px-2">{t('bank.cols.records')}</th>
            </tr></thead>
            <tbody>{data.rows.map((tx) => <TxRow key={tx.id} tx={tx} onOpen={() => setOpen(tx)} />)}</tbody>
          </table>
          {!data.rows.length && <p className="py-8 text-center text-sm text-slate-500" data-testid="bank-empty">{t('bank.empty')}</p>}
        </div>
        {open && <BankTxDialog key={open.id} tx={open} canEdit={canEdit} onClose={() => setOpen(null)} onChanged={(tx) => { setOpen(tx); load(); }} />}
      </CardContent>
    </Card>
  );
};

export default BankReconciliation;
